/*
 * Handwritten cards in the cart drawer and on the cart page - one per
 * bouquet / arrangement / bundle. (The card message is no longer asked for on
 * the product page; this is where customers write it.)
 *
 * WHERE THE TEXT GOES
 *   The line item's `Message` property. That is the same field the Globo
 *   (GPO) product-page option writes, the order queue reads per arrangement
 *   (shared/models/order.ts cardMessageFrom) and the Order Printer sheet prints
 *   (li.properties['Message']). Never rename it.
 *
 * WHICH LINES GET A CARD
 *   Products tagged "Floral Arrangement" or "Bouquet" (the queue's isFloral
 *   test), "Bundles" or "Bulk Bundles". Also any line that already has a
 *   message or came from a GPO option set.
 *
 * QUANTITY > 1
 *   One line = one card. If a line has 2+ of the same arrangement, the customer
 *   can tick "Write a separate card for each"; saving then splits the line into
 *   single units, each with its own Message (the queue lists these as
 *   "(1 of 2)", "(2 of 2)").
 *
 * Markup: the [data-vb-card] placeholders in snippets/site-template.liquid
 * (drawer, #ajaxcart-template) and templates/cart.liquid (cart page,
 * #cart-form-template). Both are rendered by wpbingo.js; cards are
 * re-initialised here whenever either re-renders.
 */
(function () {
	'use strict';

	var MAX_LEN = 200; // matches the GPO Message field
	var CARD_TAGS = ['Floral Arrangement', 'Bouquet', 'Bundles', 'Bulk Bundles'];
	var TAG_CACHE_KEY = 'vbCardHandles';
	var PLACEHOLDER = 'e.g. Happy birthday, Mum. Love always, Sam';

	var drafts = {};   // line key -> { open, split, texts[] } survives re-renders
	var floralCache = readCache();

	function readCache() {
		try { return JSON.parse(sessionStorage.getItem(TAG_CACHE_KEY) || '{}'); } catch (e) { return {}; }
	}
	function writeCache() {
		try { sessionStorage.setItem(TAG_CACHE_KEY, JSON.stringify(floralCache)); } catch (e) {}
	}

	function isFloral(handle) {
		if (!handle) return Promise.resolve(false);
		if (Object.prototype.hasOwnProperty.call(floralCache, handle)) return Promise.resolve(floralCache[handle]);
		return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
			.then(function (r) { return r.ok ? r.json() : { tags: [] }; })
			.then(function (p) {
				var tags = p.tags || [];
				var floral = tags.some(function (t) { return CARD_TAGS.indexOf(t) !== -1; });
				floralCache[handle] = floral;
				writeCache();
				return floral;
			})
			.catch(function () { return false; });
	}

	/* ---------- cart API ---------- */

	function getCart() {
		return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
			.then(function (r) { return r.json(); });
	}
	function post(url, body) {
		return fetch(url, {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(body)
		}).then(function (r) {
			if (!r.ok) return r.json().catch(function () { return {}; }).then(function (j) {
				throw new Error(j.description || j.message || ('Request failed (' + r.status + ')'));
			});
			return r.json();
		});
	}

	// Line properties with Message set (or removed when blank). Keeps every
	// other property - incl. GPO's hidden _ ones that carry add-on pricing.
	function withMessage(props, text) {
		var out = {};
		for (var k in props) {
			if (String(k).trim().toLowerCase() !== 'message') out[k] = props[k];
		}
		var t = String(text || '').trim().slice(0, MAX_LEN);
		if (t) out.Message = t;
		if (out._gpo_options) {
			try {
				var o = JSON.parse(out._gpo_options);
				if (t) o.Message = t; else delete o.Message;
				out._gpo_options = JSON.stringify(o);
			} catch (e) {}
		}
		return out;
	}

	function save(key, texts, split) {
		return getCart().then(function (cart) {
			var line = null;
			cart.items.forEach(function (i) { if (i.key === key) line = i; });
			if (!line) throw new Error('This item is no longer in your cart.');
			var props = line.properties || {};

			if (!split || line.quantity < 2) {
				return post('/cart/change.js', { id: key, quantity: line.quantity, properties: withMessage(props, texts[0]) });
			}
			// Split into single units, one card each.
			return post('/cart/change.js', { id: key, quantity: 1, properties: withMessage(props, texts[0]) })
				.then(function () {
					var items = [];
					for (var n = 1; n < line.quantity; n++) {
						items.push({ id: line.variant_id, quantity: 1, properties: withMessage(props, texts[n]) });
					}
					return post('/cart/add.js', { items: items });
				});
		});
	}

	// Re-render whichever cart views are on the page (drawer, cart page).
	function refreshCart() {
		if (window.ajaxCart && typeof window.ajaxCart.load === 'function') window.ajaxCart.load();
		if (document.querySelector('[data-cartpage-container]') && window.ajaxCartPage && typeof window.ajaxCartPage.load === 'function') {
			window.ajaxCartPage.load();
		}
	}

	/* ---------- UI ---------- */

	function el(tag, cls, text) {
		var e = document.createElement(tag);
		if (cls) e.className = cls;
		if (text != null) e.textContent = text;
		return e;
	}

	function counter(textarea, out) {
		var update = function () { out.textContent = textarea.value.length + ' / ' + MAX_LEN; };
		textarea.addEventListener('input', update);
		update();
	}

	function render(card) {
		var key = card.getAttribute('data-key');
		var qty = parseInt(card.getAttribute('data-qty'), 10) || 1;
		var savedEl = card.querySelector('[data-vb-card-saved]');
		var saved = savedEl ? savedEl.textContent.trim() : '';
		var d = drafts[key] || (drafts[key] = { open: false, split: false, texts: [] });

		card.innerHTML = '';
		card.classList.toggle('is-open', d.open);

		if (!d.open) {
			if (saved) {
				var box = el('div', 'vb-card__saved');
				box.appendChild(el('span', 'vb-card__label', 'Handwritten card'));
				box.appendChild(el('p', 'vb-card__text', saved));
				var edit = el('button', 'vb-card__link', 'Edit');
				edit.type = 'button';
				edit.addEventListener('click', function () { d.open = true; d.texts = [saved]; render(card); });
				box.appendChild(edit);
				card.appendChild(box);
			} else {
				var add = el('button', 'vb-card__add', '+ Add a handwritten card');
				add.type = 'button';
				add.addEventListener('click', function () { d.open = true; render(card); focusFirst(card); });
				card.appendChild(add);
			}
			card.appendChild(hiddenSaved(saved));
			return;
		}

		// Open: inline form
		var form = el('div', 'vb-card__form');
		var count = d.split && qty > 1 ? qty : 1;
		for (var i = 0; i < count; i++) {
			var id = 'vb-card-' + key.replace(/[^a-z0-9]/gi, '') + '-' + i;
			var label = el('label', 'vb-card__label', count > 1 ? 'Card ' + (i + 1) + ' of ' + count : 'Handwritten card');
			label.setAttribute('for', id);
			var ta = el('textarea', 'vb-card__input');
			ta.id = id;
			ta.rows = 3;
			ta.maxLength = MAX_LEN;
			ta.placeholder = PLACEHOLDER;
			// Plain free text: keep iOS/Android from treating it as a login/address
			// field (their AutoFill bar), but leave spelling help on.
			ta.setAttribute('autocomplete', 'off');
			ta.setAttribute('autocapitalize', 'sentences');
			ta.setAttribute('spellcheck', 'true');
			ta.value = d.texts[i] != null ? d.texts[i] : (i === 0 ? saved : '');
			(function (index, area) {
				area.addEventListener('input', function () { d.texts[index] = area.value; });
			})(i, ta);
			var meta = el('span', 'vb-card__count');
			counter(ta, meta);
			form.appendChild(label);
			form.appendChild(ta);
			form.appendChild(meta);
		}

		if (qty > 1) {
			var splitWrap = el('label', 'vb-card__split');
			var cb = document.createElement('input');
			cb.type = 'checkbox';
			cb.checked = d.split;
			cb.addEventListener('change', function () {
				collect(card, d);
				d.split = cb.checked;
				render(card);
				focusFirst(card);
			});
			splitWrap.appendChild(cb);
			splitWrap.appendChild(document.createTextNode(' Write a separate card for each of the ' + qty));
			form.appendChild(splitWrap);
		}

		var error = el('p', 'vb-card__error');
		error.setAttribute('role', 'alert');
		error.hidden = true;
		form.appendChild(error);

		var actions = el('div', 'vb-card__actions');
		var saveBtn = el('button', 'btn vb-card__save', 'Save card' + (count > 1 ? 's' : ''));
		saveBtn.type = 'button';
		var cancel = el('button', 'vb-card__link', 'Cancel');
		cancel.type = 'button';
		actions.appendChild(saveBtn);
		actions.appendChild(cancel);
		form.appendChild(actions);
		card.appendChild(form);
		card.appendChild(hiddenSaved(saved));

		cancel.addEventListener('click', function () {
			delete drafts[key];
			render(card);
		});

		saveBtn.addEventListener('click', function () {
			collect(card, d);
			saveBtn.disabled = true;
			saveBtn.textContent = 'Saving…';
			error.hidden = true;
			save(key, d.texts.slice(0, count), d.split && qty > 1)
				.then(function () {
					delete drafts[key]; // the line key changes once its properties do
					refreshCart();
				})
				.catch(function (err) {
					saveBtn.disabled = false;
					saveBtn.textContent = 'Save card' + (count > 1 ? 's' : '');
					error.textContent = (err && err.message) || 'That didn’t save. Please try again.';
					error.hidden = false;
				});
		});
	}

	function collect(card, d) {
		var areas = card.querySelectorAll('.vb-card__input');
		for (var i = 0; i < areas.length; i++) d.texts[i] = areas[i].value;
	}
	function hiddenSaved(text) {
		var p = el('p', 'vb-card__saved-text', text);
		p.setAttribute('data-vb-card-saved', '');
		p.hidden = true;
		return p;
	}
	function focusFirst(card) {
		var ta = card.querySelector('.vb-card__input');
		if (ta) ta.focus();
	}

	function init(root) {
		var cards = (root || document).querySelectorAll('[data-vb-card]:not([data-vb-ready])');
		Array.prototype.forEach.call(cards, function (card) {
			card.setAttribute('data-vb-ready', '1');
			var show = card.hasAttribute('data-has-message') || card.hasAttribute('data-gpo');
			var go = function () { card.hidden = false; render(card); };
			if (show) return go();
			isFloral(card.getAttribute('data-handle')).then(function (floral) { if (floral) go(); });
		});
	}

	/* ---------- boot: watch the drawer + cart page for re-renders ---------- */

	function watch() {
		var containers = document.querySelectorAll('.js-drawer [data-cart-container], [data-cartpage-container]');
		Array.prototype.forEach.call(containers, function (container) {
			init(container);
			new MutationObserver(function () { init(container); }).observe(container, { childList: true, subtree: true });
		});
	}
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
	else watch();
})();
